// @flow

import { useCallback, useMemo, useState } from 'react';
import useIntl from '../../hooks/useIntl';
import useSelectedProperty from '../../hooks/useSelectedProperty';
import useComplianceApprovalStagesQuery from '../../hooks/data-fetching/useComplianceApprovalStagesQuery';
import useComplianceApprovalCountsQuery from '../../hooks/data-fetching/useComplianceApprovalCountsQuery';
import messages from './messages';
import { buildFunnelModel } from './funnelModel';
import type { CertificationApprovalsViewModel } from './types';

export const useCertificationApprovals =
  (): CertificationApprovalsViewModel => {
    const intl = useIntl();
    const selectedProperty = useSelectedProperty();
    const organizationId = selectedProperty?.organizationId ?? null;
    const propertyId = selectedProperty?.id ?? null;

    // Step 7 — the funnel now reads two live endpoints instead of the frozen
    // fixtures: the stage list (structure, order, grouping, and the default
    // selected stage) and the per-stage counts for the selected property. The
    // counts query is keyed on the property id, so switching property refetches
    // that property's counts automatically. The fixtures remain in the repo as
    // test data; nothing imports them here any more.
    const stagesQuery = useComplianceApprovalStagesQuery();
    const countsQuery = useComplianceApprovalCountsQuery({
      organizationId,
      propertyId,
    });

    const stagesData = stagesQuery.data;
    const countsData = countsQuery.data;

    // Build the grouped view model only once both responses are in. The pure
    // transform enforces the contract rules (match by key, proportional widths,
    // hold row excluded, no status name referenced).
    const model = useMemo(
      () =>
        stagesData && countsData
          ? buildFunnelModel(stagesData, countsData)
          : null,
      [stagesData, countsData],
    );

    // Selection is a highlight only for now — the drill-down panel it will open
    // is a later step. The default comes from the stages response
    // (_defaultStageKey), never a hard-coded status name; a user click
    // overrides it.
    const [selectedStageKey, setSelectedStageKey] = useState(null);
    const onSelectStage = useCallback(
      (stageKey: string) => setSelectedStageKey(stageKey),
      [],
    );
    const effectiveSelectedStageKey =
      selectedStageKey ?? model?.defaultStageKey ?? '';

    // Resolve the selected stage's label from the model so the panel can name
    // the stage immediately, before its certs load. Every stage — the bars and
    // the hold row beneath the funnel — is included, so clicking the hold row
    // opens a correctly-titled panel too. The label comes from the data, never
    // a status string typed here.
    const stageLabelByKey = useMemo(() => {
      const map = new Map();
      (model?.groups ?? []).forEach((group) => {
        group.stages.forEach((stage) => map.set(stage.key, stage.label));
      });
      (model?.holdStages ?? []).forEach((stage) =>
        map.set(stage.key, stage.label),
      );
      return map;
    }, [model]);
    const selectedStageLabel =
      stageLabelByKey.get(effectiveSelectedStageKey) ?? '';

    // Three states, plus a distinct empty state. react-query retries transient
    // failures before reporting isError, so a true error means the data is
    // genuinely unavailable — shown as a clear message, not a blank screen or a
    // spinner that never ends. While there is no error and no model yet (either
    // request still in flight, or the property context still resolving) the
    // screen is loading.
    const isError = stagesQuery.isError || countsQuery.isError;
    const isEmpty = Boolean(model && model.totalCount === 0);
    const isLoading = !isError && !model;

    return {
      documentTitle: intl.formatMessage(messages.pageTitle),
      header: intl.formatMessage(messages.header),
      groups: model?.groups ?? [],
      holdStages: model?.holdStages ?? [],
      selectedStageKey: effectiveSelectedStageKey,
      selectedStageLabel,
      onSelectStage,
      organizationId,
      propertyId,
      isLoading,
      isError,
      errorMessage: intl.formatMessage(messages.loadError),
      isEmpty,
      emptyMessage: intl.formatMessage(messages.emptyState),
    };
  };
